import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { EngineClient } from '../engine/worker/client.ts';
import { type FieldWidget, type FitInfo, type History, type ObjectActionInfo, type ObjectRef, type OcrCutout, type PlanInfo, type ResidualInfo, type ScanChange } from '../engine/worker/protocol.ts';
import { PasswordException, pdfjsReady } from './pdfjs.ts';
import { independentEditCheck, independentExportCheck, independentFieldCheck } from './check.ts';
import { offlineReady } from './offline.ts';
import { BASE_SCALE, describeItems, openViewer, pageSizes, thumbnail, FIT_MIN, ZOOM_MAX, ZOOM_MIN, ZOOM_STEPS, type Editing, type PasswordPrompt, type Selection, type Session, type Status } from './model.ts';
import { Page } from './components/PageView.tsx';
import { TopBar } from './components/TopBar.tsx';
import { HomePage } from './components/HomePage.tsx';
import { CloseIcon } from './components/icons.tsx';
import { Card, Dock, summary, type CardContent } from './components/Panel.tsx';
import { FindCard, type FoundMatch } from './components/FindCard.tsx';
import { replaceRanges } from '../engine/edit/find.ts';
import { snapshot } from './snapshot.ts';
import { addRecent, clearRecent, listRecent, recentBytes, recentEnabled, removeRecent, setRecentEnabled, type RecentFile } from './recent.ts';
import { useTheme } from './theme.ts';
import { copySelection, spansInReadingOrder } from './clipboard.ts';
import { crossPageTransform, placeAt, topLeftUser, userAt } from './viewports.ts';
import { droppedPicture, isOwnPaste, pictureFile, rememberWritten } from './picture.ts';
import { suggestedName } from './export-name.ts';
import { debugOn, log, logCheck, logEngine, logError, logSession, setDebugOn } from './debuglog.ts';
import { DebugPanel } from './components/DebugPanel.tsx';
import { cutOut, pageKind, recognisePage, UNCERTAIN, type PageKind, type PageResult } from './ocr.ts';

type View = 'home' | 'editor';
/** a button in a message, e.g. "Do it anyway" in debug mode (D-45) */
type MessageAction = { label: string; run: () => void };
/** how long a message that confirms a change stays (AC-UX-6) */
const MESSAGE_MS = 5000;
/** The page under a window point now, and the point in that page's CSS px (undefined: no page is there). */
function pageAt(clientX: number, clientY: number): { page: number; x: number; y: number } | undefined {
  const section = document.elementsFromPoint(clientX, clientY).find((el): el is HTMLElement => el instanceof HTMLElement && /^page-\d+$/.test(el.dataset.testid ?? ''));
  const r = section?.querySelector('canvas')?.getBoundingClientRect();
  return section && r ? { page: Number(section.dataset.testid!.slice(5)), x: clientX - r.left, y: clientY - r.top } : undefined;
}
/** What removing recognised words did to the scan's size (black-and-white scans grow when stored losslessly). */
function scanNote(scan: ScanChange): string {
  const kb = (n: number) => `${Math.max(1, Math.round(n / 1024))} KB`;
  const grown = scan.after - scan.before;
  const size = !scan.before ? '' : scan.codec === 'jpeg' ? ' Only the blocks of the JPEG under the words changed.' : grown > 1024 ? ` The scan is stored again without loss, which takes ${kb(grown)} more (${kb(scan.before)} → ${kb(scan.after)}).` : '';
  return size + coveredNote(scan);
}
/** Words that could not be removed from their picture (Bug2): covered, and still in the file. */
function coveredNote(scan: ScanChange | undefined): string {
  const c = scan?.covered;
  if (!c) return '';
  const n = c.words === 1 ? 'One old word' : `${c.words} old words`;
  return ` ${n} could not be removed from ${c.words === 1 ? 'its' : 'their'} picture (${c.why}), so ${c.words === 1 ? 'it is' : 'they are'} covered with a patch instead: still in the file underneath (the check before export lists ${c.words === 1 ? 'it' : 'them'}).`;
}
/** Thrown when the user cancels a question about a run of edits in the Context Panel. */
const CANCELLED = new Error('cancelled');
/** five clicks on the homepage logo within this time turn debug mode on (D-45) */
const LOGO_CLICKS_MS = 3000;
/** In a fit zoom, from this window width (CSS px) the pages keep room for the dock and the card (D-69). */
const CARD_MIN_WINDOW = 1360;
/** The room beside the pages (CSS px), besides their own 16 px margin, for the dock alone; the card starts this far from the window's edge. */
const DOCK_ROOM = 64;
const CARD_LEFT = 76;
/** how far a paste on the copied object's own page lands from it, down and to the right (CSS px) */
const PASTE_OFFSET = 20;
/** the line editor, which the questions about an edit sit next to */
const editorAnchor = () => document.querySelector('[data-testid="edit-input"]');
/** Back to the line being edited after a question about it was cancelled. */
const refocusEditor = () => requestAnimationFrame(() => (editorAnchor() as HTMLElement | null)?.focus());
/** The engine requests that change the page: refused in View mode (D-74). Filling fields, Enhance, undo and redo are allowed. */
const PAGE_CHANGES = new Set(['apply', 'objects', 'objectsToPage', 'transformObjects', 'insertImage', 'replaceImage', 'paste']);
/** A PDF opens in View mode; the e2e suites about editing start in Edit mode (tests/e2e/fixtures.ts). */
const startMode = (): Mode => {
  try {
    return sessionStorage.getItem('flexpdf-start-mode') === 'edit' ? 'edit' : 'view';
  } catch {
    return 'view';
  }
};
type Mode = 'view' | 'edit';
import { EnhanceMenu, ExportNameForm, Modal, OverflowChoice, PasswordForm, Popover, ResidualsDialog, SubstituteChoice, ViewOnlyNotice } from './components/dialogs.tsx';

export function App() {
  const engine = useRef<EngineClient>(null);
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [session, setSession] = useState<Session>();
  const [prompt, setPrompt] = useState<PasswordPrompt>();
  /** the latest message: 'done' confirms a change and goes away by itself; 'warn' stays until dismissed (AC-UX-6) */
  const [message, setMessageState] = useState<{ text: string; kind: 'done' | 'warn'; id: number; action?: MessageAction }>();
  const setMessage = (text?: string, kind: 'done' | 'warn' = 'warn', action?: MessageAction) => setMessageState(text === undefined ? undefined : { text, kind, id: Date.now() + Math.random(), action });
  useEffect(() => {
    if (message?.kind !== 'done') return;
    const t = setTimeout(() => setMessageState((m) => (m?.id === message.id ? undefined : m)), MESSAGE_MS);
    return () => clearTimeout(t);
  }, [message?.id]);
  const [editing, setEditing] = useState<Editing>();
  /** View mode (select, copy, Enhance, Find, fill fields) or Edit mode (every change of the page), D-74 */
  const [mode, setMode] = useState<Mode>('view');
  const modeRef = useRef(mode);
  modeRef.current = mode;
  /** a change of the page was made since the file was opened: the export is suggested as -edited, else -filled (AC-MODE-3) */
  const pageChanged = useRef(false);
  /** the export's file name: asked first (unset: not asked now), then used when the copy is written */
  const [exportName, setExportName] = useState<string>();
  const exportAs = useRef('');
  const [confirm, setConfirm] = useState<{ edit: Editing; plan: Extract<PlanInfo, { kind: 'substitute' }>; fit?: FitInfo }>();
  /** the edited line would not fit: the user chooses to shrink it, let it overflow, or cancel (AC-EDIT-5) */
  const [overflow, setOverflow] = useState<{ edit: Editing; fit: FitInfo }>();
  const [selection, setSelection] = useState<Selection>();
  /** debug mode (D-45), for this tab; its panel shows the log */
  const [debug, setDebug] = useState(debugOn);
  const [debugPanel, setDebugPanel] = useState(false);
  /** export asked while the document holds forced changes (D-45): the user is asked first */
  const [forcedQuestion, setForcedQuestion] = useState(false);
  /** when the homepage logo was last clicked: five clicks within LOGO_CLICKS_MS turn debug mode on */
  const logoClicks = useRef<number[]>([]);
  const logoClick = () => {
    const now = Date.now();
    logoClicks.current = [...logoClicks.current.filter((t) => now - t < LOGO_CLICKS_MS), now];
    if (logoClicks.current.length < 5) return;
    logoClicks.current = [];
    if (!debug) setDebugOn(true);
    setDebug(true);
    setDebugPanel(true);
  };
  /** objects just moved: selected again once their page is read anew, so they can be adjusted further */
  const [reselect, setReselect] = useState<{ page: number; refs: ObjectRef[] }>();
  /** places where deleted or replaced text still occurs, shown before export (AC-DEL-3) */
  const [residuals, setResiduals] = useState<ResidualInfo[]>();
  /** how many changes the engine can undo and redo (AC-UNDO-1, AC-UNDO-2) */
  const [history, setHistory] = useState<History>({ undo: 0, redo: 0 });
  const [version, setVersion] = useState(0);
  /** changes made since the file was opened, and how many of them the last export included (AC-UX-2) */
  const [changes, setChanges] = useState({ made: 0, exported: 0 });
  /** a file to open once the user agrees to drop the changes not exported */
  const [pendingOpen, setPendingOpen] = useState<File>();
  /** a file is being dragged over the window */
  const [dragging, setDragging] = useState(false);
  /** the page in view (0-based), for the top bar */
  const [pageInView, setPageInView] = useState(0);
  /** what Enhance can do with each page read so far (AC-OCR-5) */
  const [pageKinds, setPageKinds] = useState<Record<number, PageKind>>({});
  /** counts up when recognised words changed: the pages read their text again */
  const [textVersion, setTextVersion] = useState(0);
  const [enhanceMenu, setEnhanceMenu] = useState(false);
  /** stops the Enhance run in progress */
  const [ocrRun, setOcrRun] = useState<AbortController>();
  /** a scanned page was pointed out in this document already */
  const scanNoted = useRef(false);
  /** the zoom (AC-UX-7): a fixed share of BASE_SCALE, or fit to the window's width or to a whole page; kept across files */
  const [zoom, setZoom] = useState<{ mode: 'fixed' | 'width' | 'page'; factor: number }>({ mode: 'fixed', factor: 1 });
  /** the window's size, for the fit modes */
  const [win, setWin] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    let frame = 0;
    const onResize = () => {
      if (!frame) frame = requestAnimationFrame(() => {
        frame = 0;
        setWin({ w: window.innerWidth, h: window.innerHeight });
      });
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  const fileInput = useRef<HTMLInputElement>(null);
  const unexportedRef = useRef(false);
  /**
   * what was copied, for pasting and for the card (AC-UX-10): objects copied in FlexPDF (the engine holds them; their
   * page and how often they were pasted there), or a picture file added from the dock (D-69)
   */
  const [copied, setCopied] = useState<({ kind: 'objects'; page: number; pastes: number; anchor: [number, number]; from?: string[]; ocr?: boolean } | { kind: 'file'; file: File }) & CardContent>();
  // A picture file's object URL is let go when the copy changes.
  useEffect(() => {
    const url = copied?.kind === 'file' ? copied.pic : undefined;
    return () => void (url && URL.revokeObjectURL(url));
  }, [copied]);
  /** a picture of the selected images for the card, taken once the pages show it */
  const [selectedPic, setSelectedPic] = useState<string>();
  useEffect(() => {
    if (!selection?.items.some((it) => it.kind === 'image')) return setSelectedPic(undefined);
    const id = requestAnimationFrame(() => setSelectedPic(snapshot(selection.page)));
    return () => cancelAnimationFrame(id);
  }, [selection, version]);
  /** the card shows the copy although something is selected (its chip was clicked); until the selection changes */
  const [showCopy, setShowCopy] = useState(false);
  // Find (M12, D-71): the card's Find mode, what is searched, the matches found so far and the current one.
  const [findOpen, setFindOpen] = useState(false);
  const [findFocus, setFindFocus] = useState(0);
  const [query, setQuery] = useState('');
  const [matchCase, setMatchCase] = useState(false);
  const [found, setFound] = useState<FoundMatch[]>([]);
  const [current, setCurrent] = useState(0);
  const [searching, setSearching] = useState<{ page: number; of: number }>();
  const [stoppedAt, setStoppedAt] = useState<{ page: number; of: number }>();
  const searchId = useRef(0);
  const [replacement, setReplacement] = useState('');
  /** matches Replace could not change, and why (shown in the list until the search changes) */
  const [findFailed, setFindFailed] = useState(new Map<string, { why: string; forceable?: boolean }>());
  /** after a Replace: the match to make current once found again (the next one after the replaced text) */
  const resume = useRef<{ page: number; line: number; start: number }>(undefined);
  useEffect(() => setShowCopy(false), [selection]);
  /** where the mouse last was, in window coordinates: pastes land under it (AC-PASTE-1, D-44). Worked out against the pages as they are at paste time, so a scroll or zoom since the mouse moved does not make it stale. */
  const pointer = useRef<{ x: number; y: number }>(undefined);
  /** work waiting until the opened file's first page is shown (recording it in the recent files) */
  const afterFirstPage = useRef<() => void>(undefined);
  const pendingRemember = useRef<() => Promise<void>>(undefined);
  const [theme, toggleTheme] = useTheme();
  /** the view shown, and where each view was scrolled to when it was left (AC-UX-10) */
  const [nav, setNav] = useState<{ view: View; tops: Record<View, number> }>({ view: 'home', tops: { home: 0, editor: 0 } });
  const goTo = (view: View, editorTop?: number) =>
    setNav((n) => (n.view === view ? n : { view, tops: { ...n.tops, [n.view]: window.scrollY, ...(editorTop !== undefined ? { editor: editorTop } : {}) } }));
  // The view coming in is shown where it was left.
  const firstView = useRef(true);
  useLayoutEffect(() => {
    // (Block body: scrollTo returns a value in newer browsers, which React would take for a cleanup.)
    window.scrollTo(0, nav.tops[nav.view]);
    if (firstView.current) {
      firstView.current = false;
      return;
    }
    const root = document.documentElement;
    root.classList.add('is-sliding');
    const t = setTimeout(() => root.classList.remove('is-sliding'), 450);
    return () => {
      clearTimeout(t);
      root.classList.remove('is-sliding');
    };
  }, [nav.view]);
  const [recent, setRecent] = useState<RecentFile[]>([]);
  const [recentOn, setRecentOn] = useState(recentEnabled);
  const refreshRecent = () => void listRecent().then(setRecent);
  useEffect(refreshRecent, []);
  const changed = () => setChanges((c) => ({ ...c, made: c.made + 1 }));
  /** The app's own check rejected a change (taken back already): into the debug log, with how the objects are built. */
  const checkFailed = (change: string, problems: string[], page?: number, targets?: ObjectRef[]) =>
    void (page !== undefined && targets ? engine.current!.call({ type: 'shape', pageIndex: page, targets }).then((r) => r.shapes, () => undefined) : Promise.resolve(undefined)).then((shapes) =>
      logCheck(change, problems, shapes),
    );
  /** In debug mode, a refusal or failure that is only cautious offers to do the change anyway (D-45). */
  const forceAction = (forceable: boolean | undefined, again: () => void): MessageAction | undefined => (debug && forceable ? { label: 'Do it anyway (debug)', run: again } : undefined);
  const forceableOf = (e: unknown) => (e as { forceable?: boolean }).forceable;
  /**
   * A forced change whose result the app's own check found different: kept (the user asked for it), counted as
   * forced and logged. Returns the history to show.
   */
  const keepForced = async (change: string, problems: string[], page?: number, targets?: ObjectRef[]): Promise<History> => {
    checkFailed(change, problems, page, targets);
    return (await engine.current!.call({ type: 'markForced' })).history;
  };
  /** The words added to a message about a forced change. */
  const forcedNote = (forced: string[] | undefined, problems: string[]) =>
    forced?.length || problems.length ? ` Forced in debug mode: ${[...(forced ?? []), ...problems].slice(0, 3).join('; ')}. The result may not be faithful; undo takes it back.` : '';
  useEffect(() => {
    // Closing or reloading the tab with changes not exported asks first (the browser shows its own wording).
    if (!unexportedRef.current) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  });
  // Back at the opened file (everything undone, never exported) counts as no change.
  const unexported = !!session && !session.viewOnly && changes.made !== changes.exported && !(history.undo === 0 && changes.exported === 0);
  unexportedRef.current = unexported;

  useEffect(() => {
    const client = new EngineClient();
    engine.current = client;
    // The debug log (D-46): every engine request, and errors nothing else caught.
    client.onSettled = (req, ms, res, err) => {
      if (res && PAGE_CHANGES.has(req.type)) pageChanged.current = true;
      logEngine(req, ms, res, err);
    };
    // The one place View mode is enforced: whatever the UI lets through, a change of the page never reaches the engine.
    client.refuse = (req) => (modeRef.current === 'view' && PAGE_CHANGES.has(req.type) ? 'the page cannot be changed in View mode (press Edit PDF)' : undefined);
    logSession();
    const onError = (e: ErrorEvent) => logError(e.error ?? e.message);
    const onRejection = (e: PromiseRejectionEvent) => logError(e.reason);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    Promise.all([client.call({ type: 'init', base: document.baseURI }), pdfjsReady, offlineReady()]).then(
      () => setStatus({ kind: 'ready' }),
      (e: Error) => setStatus({ kind: 'error', message: `The editor could not start: ${e.message}` }),
    );
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  /** soft: the same pages draw the new version in place (no rebuild, so no blank page meanwhile): a field change */
  const replaceSession = (next: Session | undefined, soft = false) => {
    setSession((old) => {
      if (old && old.viewer !== next?.viewer) void old.viewer.handle.destroy();
      return next;
    });
    if (!soft) setVersion((v) => v + 1);
  };
  /** where the window was scrolled when a new version of the same document was shown (B3) */
  const keepScroll = useRef<{ y: number; left: number }>(undefined);
  /**
   * A new version of the open document (a change, undo, redo): shown where the window is now. The pages are
   * built again, and a layout forced while the old ones are gone and the new not yet in (removing the focused line
   * editor does that) pulls the window up to what then fits: back at the top.
   */
  const showVersion = (next: Session, soft = false) => {
    if (!soft) keepScroll.current = { y: window.scrollY, left: document.querySelector('[data-testid="pages"]')?.scrollLeft ?? 0 };
    replaceSession(next, soft);
  };
  useLayoutEffect(() => {
    const at = keepScroll.current;
    keepScroll.current = undefined;
    if (!at) return;
    // (Sideways the pages area scrolls, a wide page; up and down the window.)
    const pages = document.querySelector('[data-testid="pages"]');
    if (pages && pages.scrollLeft !== at.left) pages.scrollLeft = at.left;
    // Block body: scrollTo returns a value in newer browsers, which React would take for a cleanup.
    if (window.scrollY !== at.y) {
      window.scrollTo(0, at.y);
    }
  }, [version]);

  const openBytes = async (name: string, data: Uint8Array, password: string | undefined, owner = false) => {
    // A file opened just before, whose first page was not shown yet, is still recorded in the recent files.
    if (afterFirstPage.current) void pendingRemember.current?.();
    afterFirstPage.current = undefined;
    pendingRemember.current = undefined;
    // Timing marks for the perf run (tests/perf/run.ts); local only.
    performance.clearMarks();
    performance.mark('flexpdf:open');
    setMessage(undefined);
    setEditing(undefined);
    setConfirm(undefined);
    setOverflow(undefined);
    setSelection(undefined);
    setResiduals(undefined);
    setHistory({ undo: 0, redo: 0 });
    setChanges({ made: 0, exported: 0 });
    setPageInView(0);
    setPageKinds({});
    setEnhanceMenu(false);
    scanNoted.current = false;
    setCopied(undefined);
    pointer.current = undefined;
    setStatus({ kind: 'busy', what: 'Opening' });
    try {
      const res = await engine.current!.call({ type: 'open', bytes: data.slice().buffer, password });
      if (res.password) {
        setPrompt({ name, data, wrong: res.password === 'wrong', owner });
        return;
      }
      setPrompt(undefined);
      // From here on the engine holds the new file: any failure must also drop the
      // old document from the page, or edits and export would go to a file the
      // user does not see (M1 review #6).
      const viewer = await openViewer(data, password);
      if ('error' in viewer) {
        if (viewer.error instanceof PasswordException) {
          replaceSession(undefined);
          setPrompt({ name, data, wrong: password !== undefined, owner });
          return;
        }
        // Neither the engine nor pdf.js can show it: a named error and no page (AC-OPEN-1).
        replaceSession(undefined);
        goTo('home');
        const reason = res.viewOnly ?? (viewer.error instanceof Error ? viewer.error.message : String(viewer.error));
        setMessage(`Could not open ${name}: ${reason}`);
        return;
      }
      const sizes = await pageSizes(viewer.doc);
      const engineless = res.pages.length === 0;
      let viewOnly = res.viewOnly;
      if (!engineless && res.pages.length !== sizes.length) {
        viewOnly = `the editing engine and the viewer disagree on the number of pages (${res.pages.length} and ${sizes.length}), so editing could change the wrong page`;
      }
      log({ kind: 'ui', action: 'opened', detail: { pages: sizes.length, engineless, viewOnly: !!viewOnly, pageCountsDiffer: !engineless && res.pages.length !== sizes.length } });
      replaceSession({ name, bytes: data, password, viewer, sizes, viewOnly, restricted: !!res.restricted && viewOnly === res.viewOnly, engineless });
      setMode(startMode());
      pageChanged.current = false;
      setNav((n) => ({ view: 'editor', tops: { ...n.tops, [n.view]: n.view === 'editor' ? 0 : window.scrollY, editor: 0 } }));
      // Into the recent files (this browser only), when the browser is idle: a picture of page 1 unless a password was needed.
      const remember = async () => {
        // No picture if the document is closed meanwhile (another file opened): the file is still listed.
        const thumb = !res.encrypted ? await thumbnail(viewer.doc).catch(() => undefined) : undefined;
        try {
          await addRecent(name, data, !!res.encrypted, thumb);
          refreshRecent();
        } catch {
          // Not kept (storage full or blocked); opening is unaffected.
        }
      };
      // After page 1 is drawn and read, so its picture does not compete with them for the pdf.js worker.
      afterFirstPage.current = () => {
        // What kind of PDF this is, for the debug log (D-46): after page 1, as the engine reads the whole file for it.
        if (!engineless) void engine.current?.call({ type: 'profile' }).then((r) => log({ kind: 'profile', profile: r.profile }), logError);
        if ('requestIdleCallback' in window) requestIdleCallback(() => void remember(), { timeout: 3000 });
        else setTimeout(() => void remember(), 500);
      };
      pendingRemember.current = remember;
      performance.mark('flexpdf:pages');
    } catch (e) {
      logError(e);
      replaceSession(undefined);
      goTo('home');
      setMessage(`Could not open ${name}: ${(e as Error).message}`);
    } finally {
      setStatus({ kind: 'ready' });
    }
  };

  const open = async (file: File) => openBytes(file.name, new Uint8Array(await file.arrayBuffer()), undefined);
  /** Opens `file`, first asking if the open document has changes not exported (AC-UX-2). */
  const requestOpen = (file: File) => {
    if (unexported) setPendingOpen(file);
    else void open(file);
  };
  /**
   * A drop: a PDF (by name or type) is opened; a picture (D-70) dropped on a page is put there, centred on the drop
   * point, dropped anywhere else (the Context Panel, beside the pages) it becomes the copy, ready to paste.
   */
  const dropped = (dt: DataTransfer, x: number, y: number) => {
    const pdf = [...dt.files].find((f) => /\.pdf$/i.test(f.name) || f.type === 'application/pdf');
    if (pdf) return requestOpen(pdf);
    const picture = droppedPicture(dt);
    if (picture === 'link') return setMessage('The site gave only a link to this picture, and FlexPDF never downloads anything. Use "Copy image" on the site and paste it here (Ctrl+V), or save the picture and drop the file.');
    if (!picture) return setMessage(dt.files[0] ? `"${dt.files[0].name}" is not a PDF or a picture, so it was not used.` : 'Only a PDF or a picture can be dropped here.');
    if (!session) return setMessage('Open a PDF first, then drop the picture onto a page.');
    if (!canEdit) return setMessage('This document can only be viewed, so a picture cannot be added.');
    if (mode !== 'edit') return setMessage('To add a picture, press Edit PDF first.');
    const onPanel = document.elementFromPoint(x, y)?.closest('.dock, .card-wrap');
    const under = !onPanel ? pageAt(x, y) : undefined;
    const at = under && userAt(under.page, under.x, under.y);
    void takePicture(picture, under && at ? { page: under.page, at } : undefined);
  };

  // Drag and drop anywhere on the window (AC-UX-2).
  useEffect(() => {
    let depth = 0;
    // Files, or a picture dragged from a site as a link (told why it cannot be used, D-70).
    const hasFiles = (e: DragEvent) => !!e.dataTransfer && [...e.dataTransfer.types].some((t) => t === 'Files' || t === 'text/uri-list');
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth++;
      setDragging(true);
    };
    const leave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (!depth) setDragging(false);
    };
    const over = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer!.dropEffect = 'copy';
    };
    const drop = (e: DragEvent) => {
      // Never let the browser open the file itself (it would leave the app).
      e.preventDefault();
      depth = 0;
      setDragging(false);
      if (e.dataTransfer) dropRef.current(e.dataTransfer, e.clientX, e.clientY);
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('dragover', over);
      window.removeEventListener('drop', drop);
    };
  }, []);
  const dropRef = useRef(dropped);
  dropRef.current = status.kind === 'ready' ? dropped : () => setMessage('Please wait until the current action has finished, then drop the file again.');

  // The page in view: the one across the middle of the window (pages are in order, so a binary search).
  useEffect(() => {
    if (!session) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const pages = document.querySelectorAll<HTMLElement>('[data-testid="pages"] > section');
      const mid = window.innerHeight / 2;
      let lo = 0;
      let hi = pages.length - 1;
      while (lo < hi) {
        const m = (lo + hi) >> 1;
        if (pages[m].getBoundingClientRect().bottom < mid) lo = m + 1;
        else hi = m;
      }
      setPageInView(lo);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [session?.sizes]);

  /** A page read its text: what Enhance can do with it; the first scanned page offers Enhance (AC-OCR-5). */
  const pageRead = (page: number, kind: PageKind, recognised: boolean) => {
    setPageKinds((k) => (k[page] === kind ? k : { ...k, [page]: kind }));
    if (kind === 'none' || recognised || scanNoted.current || !canEdit) return;
    scanNoted.current = true;
    setMessage(
      kind === 'layer' ? 'This page is a scan with its own invisible text. Enhance can recognise it again.' : 'This page is a scan: its text is a picture. Enhance recognises it, so it can be selected.',
      'warn',
      { label: 'Enhance', run: () => setEnhanceMenu(true) },
    );
  };

  /**
   * Enhance (D-50, AC-OCR-5): recognise the text of the page in view or of every scanned page. Pages with their own
   * invisible text keep it unless `again`. Nothing is kept from a cancelled run; the words go to the engine together
   * at the end (they are not written to the PDF).
   */
  /**
   * Starts editing a line, except the scanner's hidden text over a scan (Bug2): changing it would not change what is
   * seen, so the user is pointed to recognising the page instead.
   */
  const startEditing = (e?: Editing) => {
    if (e && mode !== 'edit') return;
    if (e && e.line.spans.length && pageKinds[e.page] === 'layer' && e.span.invisible) {
      setSelection(undefined);
      setMessage('This is the hidden text the scanner put over the picture: changing it would not change what you see. Recognise the page again to edit the words on the picture itself.', 'warn', { label: 'Recognise again', run: () => void enhance('page', true) });
      return;
    }
    setEditing(e);
  };

  const enhance = async (which: 'page' | 'all', again = false) => {
    const s = session!;
    setEnhanceMenu(false);
    setSelection(undefined);
    const run = new AbortController();
    setOcrRun(run);
    const pages = which === 'page' ? [pageInView] : s.sizes.map((_, i) => i);
    const found: { page: number; result: PageResult }[] = [];
    const kept: number[] = [];
    const t0 = performance.now();
    setStatus({ kind: 'busy', what: 'Recognising text' });
    try {
      for (const [k, i] of pages.entries()) {
        const info = await engine.current!.call({ type: 'spans', pageIndex: i });
        const pageArea = s.sizes[i].width * s.sizes[i].height;
        const kind = pageKind(info.spans, info.images, pageArea);
        if (kind === 'none' || (kind === 'layer' && !again)) {
          if (kind === 'layer') kept.push(i);
          continue;
        }
        if (run.signal.aborted) throw new DOMException('cancelled', 'AbortError');
        const where = pages.length > 1 ? `page ${i + 1} (${k + 1} of ${pages.length}), ` : '';
        const result = await recognisePage(s.viewer.doc, i, info.images, pageArea, run.signal, (p) => setStatus({ kind: 'busy', what: `Recognising text: ${where}${Math.round(p * 100)} %` }));
        found.push({ page: i, result });
      }
      for (const f of found) await engine.current!.call({ type: 'ocr', pageIndex: f.page, words: f.result.words, image: f.result.image });
      // Into the debug log: counts and times only, never the words.
      log({ kind: 'ui', action: 'enhance', detail: { pages: found.length, kept: kept.length, words: found.reduce((a, f) => a + f.result.words.length, 0), turned: found.filter((f) => f.result.turn).length, seconds: Math.round((performance.now() - t0) / 100) / 10 } });
      setTextVersion((v) => v + 1);
      const words = found.reduce((a, f) => a + f.result.words.length, 0);
      const uncertain = found.reduce((a, f) => a + f.result.words.filter((w) => w.confidence < UNCERTAIN).length, 0);
      const keptNote = kept.length ? ` ${kept.length === 1 ? 'One page keeps' : `${kept.length} pages keep`} ${kept.length === 1 ? 'its' : 'their'} own invisible text.` : '';
      if (!found.length) setMessage(which === 'page' ? 'This page is not a scan, so there is nothing to recognise.' : `No scanned pages to recognise.${keptNote}`);
      else
        setMessage(
          `Recognised ${words} word${words === 1 ? '' : 's'}${found.length > 1 ? ` on ${found.length} pages` : ''}: they can now be selected, edited, moved and deleted, and are searchable in the exported copy.${uncertain ? ` ${uncertain} uncertain word${uncertain === 1 ? ' is' : 's are'} marked.` : ''}${keptNote}`,
          'done',
        );
    } catch (e) {
      if (run.signal.aborted) setMessage('Enhance was cancelled. Nothing was recognised or changed.', 'done');
      else {
        logError(e);
        setMessage(`The text could not be recognised: ${(e as Error).message}. Nothing was changed.`);
      }
    } finally {
      setOcrRun(undefined);
      setStatus({ kind: 'ready' });
    }
  };

  /** The engine already holds a change the UI could not finish (the check or the viewer failed): take it back, so the engine and the shown document agree. */
  const rollback = async () => {
    try {
      setHistory((await engine.current!.call({ type: 'revert' })).history);
    } catch {
      // Nothing to take back.
    }
  };

  const apply = async (edit: Editing, fit?: 'shrink' | 'overflow') => {
    const force = !!edit.force;
    const s = session!;
    setStatus({ kind: 'busy', what: 'Applying edit' });
    setConfirm(undefined);
    setOverflow(undefined);
    let engaged = false; // the engine changed the document: if the UI then fails, it must be taken back
    try {
      const res = await engine.current!.call({ type: 'apply', pageIndex: edit.page, spanIndex: edit.span.index, text: edit.text, fit, force, face: edit.face });
      engaged = true;
      const next = new Uint8Array(res.bytes);
      // Independent check with pdf.js; undo the edit if anything outside it changed.
      const problems = await independentEditCheck(s.bytes, next, res.pageIndex, res.regions, s.password, res.replace);
      if (problems.length && !force) {
        setHistory((await engine.current!.call({ type: 'revert' })).history);
        checkFailed('edit', problems, edit.page, [{ kind: 'span', spanIndex: edit.span.index }]);
        setMessage(`The edit was undone: an independent check found that text outside the edit changed (${problems.slice(0, 2).join('; ')}). The document is unchanged.`);
      } else {
        const viewer = await openViewer(next, s.password);
        if ('error' in viewer) throw viewer.error;
        showVersion({ ...s, bytes: next, viewer });
        setEditing(undefined);
        setHistory(problems.length ? await keepForced('edit', problems, edit.page, [{ kind: 'span', spanIndex: edit.span.index }]) : res.history);
        changed();
        const note = forcedNote(res.forced, problems);
        setMessage(`Changed "${edit.line.text}" to "${edit.text}".${note}${coveredNote(res.scan)}`, note || res.scan?.covered ? 'warn' : 'done');
      }
    } catch (e) {
      logError(e);
      if (engaged) await rollback();
      setMessage(`The edit was not applied: ${(e as Error).message}. The document is unchanged.`, 'warn', forceAction(forceableOf(e), () => void apply({ ...edit, force: true }, fit)));
    }
    setStatus({ kind: 'ready' });
  };

  /** A question about an edit in the Context Panel's card; its answer resolves the promise. */
  const [cardQuestion, setCardQuestion] = useState<React.ReactNode>();
  const askInCard = <T,>(build: (answer: (a: T) => void) => React.ReactNode) =>
    new Promise<T>((resolve) =>
      setCardQuestion(
        build((a) => {
          setCardQuestion(undefined);
          resolve(a);
        }),
      ),
    );

  /** What a run of changes has done so far: how many (undo steps), the document after them, the history. */
  type Progress = { done: number; bytes: Uint8Array; history?: History };
  /**
   * Answers that cover the rest of a run (Replace all, D-71): a font question once per font (by the font's name), the
   * room question when "the same for the rest" was ticked. A "no" leaves those lines out instead of stopping the run.
   */
  type RunAnswers = { fonts: Map<string, 'yes' | { face: string } | 'no'>; room?: 'shrink' | 'overflow' | 'cancel' };

  /**
   * Edits lines on page `page` (each: its first span, its text now, its new text), from the last in the page's content
   * to the first (an edit shifts only the spans after it): each found again, planned, asked about in the card, applied
   * and checked with pdf.js, counted into `p`. Throws on a cancel (CANCELLED), a refusal or a failed check; the caller
   * takes back what `p` counts.
   */
  const editLines = async (page: number, changes: { first: number; was: string; text: string }[], p: Progress, opts: { answers?: RunAnswers; force?: boolean } = {}) => {
    const s = session!;
    const eng = engine.current!;
    const { answers, force } = opts;
    const leftOut = (why: string) => new Error(why);
    for (const c of [...changes].sort((a, b) => b.first - a.first)) {
      const info = await eng.call({ type: 'spans', pageIndex: page });
      const line = info.lines.find((l) => l.spans[0] === c.first);
      if (line?.text !== c.was) throw new Error(`the line "${c.was}" could not be found again after the other edits`);
      let face: string | undefined;
      let { plan, fit } = await eng.call({ type: 'plan', pageIndex: page, spanIndex: c.first, text: c.text, force });
      while (plan.kind === 'substitute') {
        const q = plan;
        const known = answers?.fonts.get(q.from);
        if (known === 'no') throw leftOut(`left out: no substitute was accepted for the font ${q.from}`);
        if (known === 'yes' || (known && known.face === face)) break;
        let a: 'yes' | 'no' | { face: string };
        if (known) a = known;
        else {
          a = await askInCard<'yes' | 'no' | { face: string }>((answer) => (
            <>
              <p className="card-note-plain">
                For "{c.was}":{answers ? ` your answer counts for every match in ${q.from}.` : ''}
              </p>
              <SubstituteChoice plan={q} onYes={() => answer('yes')} onNo={() => answer('no')} onPick={(f) => answer({ face: f })} />
            </>
          ));
          if (answers && a !== 'yes' && a !== 'no') {
            // A picked face is planned again (it may change the grade); its "yes" then covers the font.
            face = a.face;
            ({ plan, fit } = await eng.call({ type: 'plan', pageIndex: page, spanIndex: c.first, text: c.text, face, force }));
            continue;
          }
          if (answers && (a === 'yes' || a === 'no')) answers.fonts.set(q.from, a === 'yes' && face ? { face } : a);
        }
        if (a === 'no') throw answers ? leftOut(`left out: no substitute was accepted for the font ${q.from}`) : CANCELLED;
        if (a === 'yes') break;
        face = a.face;
        ({ plan, fit } = await eng.call({ type: 'plan', pageIndex: page, spanIndex: c.first, text: c.text, face, force }));
      }
      if (plan.kind === 'refuse') throw Object.assign(new Error(`"${c.was}" can't be changed faithfully: ${plan.reasons.join('; ')}`), { forceable: plan.forceable });
      let room: 'shrink' | 'overflow' | undefined;
      if (fit) {
        const f = fit;
        let a = answers?.room;
        if (!a) {
          let forRest = false;
          a = await askInCard<'shrink' | 'overflow' | 'cancel'>((answer) => (
            <>
              <p className="card-note-plain">For "{c.was}":</p>
              <OverflowChoice fit={f} onShrink={() => answer('shrink')} onKeep={() => answer('overflow')} onCancel={() => answer('cancel')} />
              {answers && (
                <label className="find-option card-note-plain">
                  <input data-testid="room-for-rest" type="checkbox" onChange={(e) => (forRest = e.target.checked)} /> The same for the rest
                </label>
              )}
            </>
          ));
          if (answers && forRest) answers.room = a;
        }
        if (a === 'cancel') throw answers ? leftOut(`left out: "${c.was}" would no longer fit its place`) : CANCELLED;
        room = a;
      }
      const res = await eng.call({ type: 'apply', pageIndex: page, spanIndex: c.first, text: c.text, fit: room, face, force });
      p.done++;
      p.history = res.history;
      const next = new Uint8Array(res.bytes);
      const problems = await independentEditCheck(p.bytes, next, res.pageIndex, res.regions, s.password, res.replace);
      // Forced (debug mode, D-45): kept, counted and logged; otherwise a different result is not kept.
      if (problems.length && force) p.history = await keepForced('edit', problems, page, [{ kind: 'span', spanIndex: c.first }]);
      else if (problems.length) throw new Error(`an independent check found that text outside the edit of "${c.was}" changed (${problems.slice(0, 2).join('; ')})`);
      p.bytes = next;
    }
  };

  /** A run of changes done: joined into one undo step and shown. */
  const finishChanges = async (p: Progress) => {
    const s = session!;
    if (p.done > 1) p.history = (await engine.current!.call({ type: 'mergeLast', count: p.done })).history;
    const viewer = await openViewer(p.bytes, s.password);
    if ('error' in viewer) throw viewer.error;
    showVersion({ ...s, bytes: p.bytes, viewer });
    setHistory(p.history!);
    changed();
  };

  /** A run of changes that failed: all of it taken back (joined, then reverted: nothing to redo). */
  const takeBack = async (p: Progress) => {
    setCardQuestion(undefined);
    if (!p.done) return;
    try {
      if (p.done > 1) await engine.current!.call({ type: 'mergeLast', count: p.done });
      setHistory((await engine.current!.call({ type: 'revert' })).history);
    } catch (u) {
      logError(u);
    }
  };

  /**
   * The selected lines edited in the Context Panel (D-69 step 2): together one undo step; the selection is kept. All
   * or nothing: a cancel, a refusal or a failed check takes back the lines already changed.
   */
  const applyLines = async (sel: Selection, texts: string[]) => {
    const changes = spansInReadingOrder(sel.items)
      .map((it, k) => ({ first: it.pieces[0].index, was: it.line.text, text: texts[k] }))
      .filter((c) => c.text !== c.was);
    if (!changes.length) return;
    setStatus({ kind: 'busy', what: changes.length === 1 ? 'Applying edit' : `Editing ${changes.length} lines` });
    const p: Progress = { done: 0, bytes: session!.bytes };
    try {
      await editLines(sel.page, changes, p);
      await finishChanges(p);
      // The selection is kept (found by the lines' new text).
      const now = await engine.current!.call({ type: 'spans', pageIndex: sel.page });
      const wanted = new Set(texts);
      setReselect({ page: sel.page, refs: now.lines.filter((l) => wanted.has(l.text)).map((l) => ({ kind: 'span', spanIndex: l.spans[0] })) });
      setMessage(changes.length === 1 ? `Changed "${changes[0].was}" to "${changes[0].text}".` : `Edited ${changes.length} lines.`, 'done');
    } catch (e) {
      await takeBack(p);
      if (e === CANCELLED) setMessage('Nothing was changed.', 'done');
      else {
        logError(e);
        setMessage(`The lines were not changed: ${(e as Error).message}. The document is unchanged.`);
      }
    }
    setStatus({ kind: 'ready' });
  };

  /**
   * The copy's text changed in the Context Panel (D-69 step 3): Paste puts the new text in with the copied lines' look
   * (pasted, then edited); the computer's clipboard gets the new text.
   */
  const updateCopy = async (texts: string[]) => {
    if (copied?.kind !== 'objects') return;
    setCopied({ ...copied, from: copied.from ?? copied.lines, lines: texts });
    const text = texts.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      rememberWritten(text, undefined);
      setMessage('The copy was changed: Paste puts in the new text, with the same look. The clipboard holds the new text too.', 'done');
    } catch {
      setMessage("The copy was changed: Paste puts in the new text, with the same look. The computer's clipboard was not updated (the browser did not allow it).", 'warn');
    }
  };

  /** face: a substitute picked in the font question; planned again with it. */
  const submit = async (force = false, face?: string) => {
    if (!editing) return;
    const edit = { ...editing, force: force || undefined, face };
    if (editing.text === editing.line.text) {
      setEditing(undefined);
      return;
    }
    setStatus({ kind: 'busy', what: 'Checking edit' });
    let plan: PlanInfo;
    let fit: FitInfo | undefined;
    try {
      ({ plan, fit } = await engine.current!.call({ type: 'plan', pageIndex: edit.page, spanIndex: edit.span.index, text: edit.text, force, face }));
    } catch (e) {
      logError(e);
      setStatus({ kind: 'ready' });
      setMessage(`This edit could not be checked: ${(e as Error).message}. Nothing was changed.`);
      return;
    }
    setStatus({ kind: 'ready' });
    if (plan.kind === 'refuse') setMessage(`This edit can't be done faithfully: ${plan.reasons.join('; ')}. Nothing was changed.`, 'warn', forceAction(plan.forceable, () => void submit(true)));
    else if (plan.kind === 'substitute') setConfirm({ edit, plan, fit });
    else if (fit) setOverflow({ edit, fit });
    else await apply(edit);
  };

  /** Delete or move the selected lines and images (one change); checked like an edit, taken back if pdf.js reads anything else changed. */
  const applyObject = async (sel: Selection, action: ObjectActionInfo, force = false) => {
    const s = session!;
    // A span target stands for its whole line (the engine acts on every piece).
    const targets: ObjectRef[] = sel.items.map((it) => (it.kind === 'span' ? { kind: 'span', spanIndex: it.pieces[0].index } : { kind: 'image', imageIndex: it.image.index }));
    const what = describeItems(sel.items);
    setStatus({ kind: 'busy', what: action.kind === 'delete' ? 'Deleting' : 'Moving' });
    setSelection(undefined);
    let engaged = false; // the engine changed the document: if the UI then fails, it must be taken back
    try {
      // Moving recognised words: their ink, cut out of the page as drawn, goes along (M6 step 6).
      const cutouts = action.kind === 'move' ? await inkOf(sel) : undefined;
      const res = await engine.current!.call({ type: 'objects', pageIndex: sel.page, targets, action, force, cutouts });
      engaged = true;
      const next = new Uint8Array(res.bytes);
      const replace = res.removed?.map((from) => ({ from, to: '' }));
      const problems = await independentEditCheck(s.bytes, next, res.pageIndex, res.regions, s.password, replace);
      if (problems.length && !force) {
        setHistory((await engine.current!.call({ type: 'revert' })).history);
        checkFailed('change', problems, sel.page, targets);
        setMessage(`The change was undone: an independent check found that something else changed (${problems.slice(0, 2).join('; ')}). The document is unchanged.`);
      } else {
        const viewer = await openViewer(next, s.password);
        if ('error' in viewer) throw viewer.error;
        showVersion({ ...s, bytes: next, viewer });
        setHistory(problems.length ? await keepForced('change', problems, sel.page, targets) : res.history);
        changed();
        if (action.kind === 'move' && res.moved?.length) setReselect({ page: res.pageIndex, refs: res.moved });
        const lone = sel.items.length === 1 ? sel.items[0] : undefined;
        const note = forcedNote(res.forced, problems);
        if (note) setMessage(`${action.kind === 'move' ? 'Moved' : 'Deleted'} ${what}.${note}`);
        else if (res.scan && action.kind === 'move') setMessage(`Moved ${what}: its ink is drawn at the new place, and its pixels are gone from where it was.${scanNote(res.scan)}`, 'done');
        else if (res.scan?.covered && !res.scan.before) setMessage(`Deleted ${what}.${scanNote(res.scan)}`, 'warn');
        else if (res.scan) setMessage(`Removed ${what} from the scan: its pixels are now the paper's colour, in the image itself.${scanNote(res.scan)}`, res.scan.covered ? 'warn' : 'done');
        else if (action.kind === 'move') setMessage(`Moved ${what}.`, 'done');
        else if (sel.items.every((it) => it.kind === 'span' && it.pieces.every((x) => x.invisible))) setMessage(`Deleted ${what}. It was invisible text (such as an OCR text layer): what the page shows, including the pixels of any image under it, is unchanged.`);
        else if (res.layer?.kept.length) setMessage(`Deleted ${what}${res.layer.deleted ? ' and part of the invisible text layer over the image' : ''}. Invisible text over the image could not all be removed and is still in the document: ${res.layer.kept.map((t) => `"${t}"`).join(', ')}.`);
        else if (res.layer?.deleted) setMessage(`Deleted ${lone ? 'the image' : what} and the invisible text layer over ${lone ? 'it' : 'the image'}.`, 'done');
        else setMessage(`Deleted ${what}.`, 'done');
      }
    } catch (e) {
      logError(e);
      if (engaged) await rollback();
      setMessage(`${action.kind === 'delete' ? 'Could not delete' : 'Could not move'} ${what}: ${(e as Error).message}. The document is unchanged.`, 'warn', forceAction(forceableOf(e), () => void applyObject(sel, action, true)));
    }
    setStatus({ kind: 'ready' });
  };

  // Undo / redo, and Delete / Escape / Enter on the selection (not while typing in a field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ctrl + / Ctrl − / Ctrl 0 zoom the document, not the browser (AC-UX-7).
      if ((e.ctrlKey || e.metaKey) && !e.altKey && session && nav.view === 'editor' && ['+', '=', '-', '_', '0'].includes(e.key)) {
        e.preventDefault();
        if (e.key === '0') changeZoom({ mode: 'fixed', factor: 1 });
        else zoomStep(e.key === '-' || e.key === '_' ? -1 : 1);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'o') {
        // The app's Open, not the browser's.
        e.preventDefault();
        if (status.kind === 'ready') (nav.view === 'home' ? document.querySelector<HTMLInputElement>('[data-testid="start-file-input"]') : fileInput.current)?.click();
        return;
      }
      // Nothing below acts on the editor while the homepage shows, or behind a question that has to be answered first.
      if (nav.view !== 'editor' || prompt || pendingOpen || debugPanel || forcedQuestion) return;
      // Escape leaves the line editor wherever the focus is (B4): back from another window the field has lost it
      // (the field handles its own Escape; a question about the edit, its own).
      if (e.key === 'Escape' && editing && !e.defaultPrevented && status.kind === 'ready' && !confirm && !overflow && !residuals) {
        e.preventDefault();
        setEditing(undefined);
        return;
      }
      // Ctrl+F: Find in the Context Panel (the browser's own find cannot see the document's text).
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'f' && session && !editing) {
        e.preventDefault();
        setFindOpen(true);
        setFindFocus((n) => n + 1);
        return;
      }
      if (e.key === 'Escape' && findOpen && !editing && !confirm && !overflow && !residuals && !cardQuestion) {
        e.preventDefault();
        setFindOpen(false);
        return;
      }
      if (editing || status.kind !== 'ready' || confirm || overflow || residuals || (e.target instanceof HTMLElement && e.target.closest('input, textarea, select'))) return;
      const key = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && !e.altKey && (key === 'z' || key === 'y')) {
        e.preventDefault();
        const redo = key === 'y' || e.shiftKey;
        if (redo ? history.redo : history.undo) void step(redo ? 'redo' : 'undo');
        return;
      }
      // Ctrl+V is handled by the paste event (it also brings pictures from outside, D-70).
      if (!selection) return;
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        void copy(selection);
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        if (mode === 'edit') void applyObject(selection, { kind: 'delete' });
      } else if (e.key === 'Escape' || e.key === 'Enter') {
        // Enter ends the selection; on a focused text or image area it must not click it (which would select it
        // again), but on any other button (Export, Undo, Edit, Delete …) it must still press that button.
        const onControl = e.target instanceof HTMLElement && !!e.target.closest('button, a, select') && !e.target.closest('.obj');
        if (e.key === 'Enter' && onControl) return;
        e.preventDefault();
        setSelection(undefined);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  /**
   * Move the selection to another page (AC-XPAGE-1/2), through T (its page's user space → the other's); both pages
   * checked with pdf.js, taken back if anything else changed.
   */
  const applyToPage = async (sel: Selection, to: number, T: number[], force = false) => {
    const s = session!;
    const targets: ObjectRef[] = sel.items.map((it) => (it.kind === 'span' ? { kind: 'span', spanIndex: it.pieces[0].index } : { kind: 'image', imageIndex: it.image.index }));
    const what = describeItems(sel.items);
    setStatus({ kind: 'busy', what: 'Moving' });
    setSelection(undefined);
    let engaged = false; // the engine changed the document: if the UI then fails, it must be taken back
    try {
      const res = await engine.current!.call({ type: 'objectsToPage', pageIndex: sel.page, toPage: to, targets, transform: T, force });
      engaged = true;
      const next = new Uint8Array(res.bytes);
      const problems = [
        ...(await independentEditCheck(s.bytes, next, res.pageIndex, res.regions, s.password, res.removed.map((from) => ({ from, to: '' })))),
        ...(await independentEditCheck(s.bytes, next, res.toPage, res.toRegions, s.password)),
      ];
      if (problems.length && !force) {
        setHistory((await engine.current!.call({ type: 'revert' })).history);
        checkFailed('move', problems, sel.page, targets);
        setMessage(`The move was undone: an independent check found that something else changed (${problems.slice(0, 2).join('; ')}). The document is unchanged.`);
      } else {
        const viewer = await openViewer(next, s.password);
        if ('error' in viewer) throw viewer.error;
        showVersion({ ...s, bytes: next, viewer });
        setHistory(problems.length ? await keepForced('move', problems, sel.page, targets) : res.history);
        changed();
        if (res.moved.length) setReselect({ page: res.toPage, refs: res.moved });
        const note = forcedNote(res.forced, problems);
        setMessage(`Moved ${what} to page ${res.toPage + 1}.${note}`, note ? 'warn' : 'done');
      }
    } catch (e) {
      logError(e);
      if (engaged) await rollback();
      setMessage(`Could not move ${what} to page ${to + 1}: ${(e as Error).message}. The document is unchanged.`, 'warn', forceAction(forceableOf(e), () => void applyToPage(sel, to, T, true)));
    }
    setStatus({ kind: 'ready' });
  };

  /**
   * Fill in a form field (M8, AC-ACRO-4). Checked with pdf.js: the pages' own text unchanged outside the field's
   * widgets, and the field's values read back as the engine wrote them; otherwise the change is taken back.
   */
  const fill = async (f: FieldWidget, value: string | string[] | boolean) => {
    const s = session!;
    setStatus({ kind: 'busy', what: 'Filling in' });
    let engaged = false;
    try {
      const res = await engine.current!.call({ type: 'fill', widget: f.id, value });
      engaged = true;
      const next = new Uint8Array(res.bytes);
      const problems: string[] = [];
      for (const p of new Set(res.widgets.map((w) => w.pageIndex))) {
        const quads = res.widgets.filter((w) => w.pageIndex === p).map((w): [number, number][] => [[w.rect[0], w.rect[1]], [w.rect[2], w.rect[1]], [w.rect[2], w.rect[3]], [w.rect[0], w.rect[3]]]);
        problems.push(...(await independentEditCheck(s.bytes, next, p, [quads], s.password)));
      }
      problems.push(...(await independentFieldCheck(next, s.password, res.widgets)));
      if (problems.length) {
        setHistory((await engine.current!.call({ type: 'revert' })).history);
        setMessage(`The change to ${f.name} was undone: an independent check found a problem (${problems.slice(0, 2).join('; ')}). The document is unchanged.`);
      } else {
        const viewer = await openViewer(next, s.password);
        if ('error' in viewer) throw viewer.error;
        // The pages stay and draw the new version over the old picture: a field change does not blink the page.
        showVersion({ ...s, bytes: next, viewer }, true);
        setHistory(res.history);
        changed();
        setMessage(`Filled in ${f.name}.`, 'done');
      }
    } catch (e) {
      logError(e);
      if (engaged) await rollback();
      setMessage(`Could not fill in ${f.name}: ${(e as Error).message}. The document is unchanged.`);
    }
    setStatus({ kind: 'ready' });
  };

  /**
   * A picture in a signature field (D-65, AC-SIG-1): any picture the browser reads (WebP and the like made PNG), fitted
   * in the field by the engine; checked with pdf.js like a fill. Allowed in View mode. Not a digital signature.
   */
  const sign = async (f: FieldWidget, blob: File) => {
    const s = session!;
    setStatus({ kind: 'busy', what: 'Adding the signature picture' });
    let engaged = false;
    try {
      const file = await pictureFile(blob, blob.name);
      const res = await engine.current!.call({ type: 'signImage', widget: f.id, data: await file.arrayBuffer() });
      engaged = true;
      const next = new Uint8Array(res.bytes);
      const [x0, y0, x1, y1] = f.rect;
      const problems = [...(await independentEditCheck(s.bytes, next, f.pageIndex, [[[[x0, y0], [x1, y0], [x1, y1], [x0, y1]]]], s.password)), ...(await independentFieldCheck(next, s.password, res.widgets))];
      if (problems.length) {
        setHistory((await engine.current!.call({ type: 'revert' })).history);
        setMessage(`The signature picture was taken out again: an independent check found a problem (${problems.slice(0, 2).join('; ')}). The document is unchanged.`);
      } else {
        const viewer = await openViewer(next, s.password);
        if ('error' in viewer) throw viewer.error;
        showVersion({ ...s, bytes: next, viewer }, true);
        setHistory(res.history);
        changed();
        setMessage(`Put the picture in ${f.name}. It is a picture of a signature, not a digital signature.`, 'done');
      }
    } catch (e) {
      logError(e);
      if (engaged) await rollback();
      setMessage(`Could not put the picture in ${f.name}: ${(e as Error).message}. The document is unchanged.`);
    }
    setStatus({ kind: 'ready' });
  };

  /** Insert a PNG or JPEG on the page in view (M9, AC-IMG-3), selected afterwards; checked with pdf.js like a paste. */
  /** Draws a picture file on a page (M9): at `place` (centred, user space), else in the middle of the page in view. */
  const insertImage = async (file: File, place?: { page: number; at?: [number, number] }) => {
    const s = session!;
    setStatus({ kind: 'busy', what: 'Inserting the image' });
    setSelection(undefined);
    let engaged = false;
    try {
      const res = await engine.current!.call({ type: 'insertImage', pageIndex: place?.page ?? pageInView, data: await file.arrayBuffer(), at: place?.at });
      engaged = true;
      const next = new Uint8Array(res.bytes);
      const problems = await independentEditCheck(s.bytes, next, res.pageIndex, res.regions, s.password);
      if (problems.length) {
        setHistory((await engine.current!.call({ type: 'revert' })).history);
        setMessage(`The image was not inserted: an independent check found that something else changed (${problems.slice(0, 2).join('; ')}). The document is unchanged.`);
      } else {
        const viewer = await openViewer(next, s.password);
        if ('error' in viewer) throw viewer.error;
        showVersion({ ...s, bytes: next, viewer });
        setHistory(res.history);
        changed();
        setReselect({ page: res.pageIndex, refs: [{ kind: 'image', imageIndex: res.imageIndex }] });
        const note = forcedNote(res.forced, []);
        setMessage(`Inserted ${file.name} on page ${res.pageIndex + 1}.${note}`, note ? 'warn' : 'done');
      }
    } catch (e) {
      logError(e);
      if (engaged) await rollback();
      setMessage(`Could not insert the image: ${(e as Error).message}. The document is unchanged.`);
    }
    setStatus({ kind: 'ready' });
  };

  /** Replace a selected image by a PNG or JPEG file (M9, AC-IMG-2); checked with pdf.js like a move. */
  const replaceImage = async (sel: Selection, file: File) => {
    const it = sel.items[0];
    if (it?.kind !== 'image') return;
    const s = session!;
    setStatus({ kind: 'busy', what: 'Replacing the image' });
    setSelection(undefined);
    let engaged = false;
    try {
      const res = await engine.current!.call({ type: 'replaceImage', pageIndex: sel.page, imageIndex: it.image.index, data: await file.arrayBuffer() });
      engaged = true;
      const next = new Uint8Array(res.bytes);
      const problems = await independentEditCheck(s.bytes, next, res.pageIndex, res.regions, s.password);
      if (problems.length) {
        setHistory((await engine.current!.call({ type: 'revert' })).history);
        setMessage(`The replacement was undone: an independent check found that something else changed (${problems.slice(0, 2).join('; ')}). The document is unchanged.`);
      } else {
        const viewer = await openViewer(next, s.password);
        if ('error' in viewer) throw viewer.error;
        showVersion({ ...s, bytes: next, viewer }, true);
        setHistory(res.history);
        changed();
        const note = forcedNote(res.forced, []);
        setMessage(`Replaced the image with ${file.name}.${note}`, note ? 'warn' : 'done');
      }
    } catch (e) {
      logError(e);
      if (engaged) await rollback();
      setMessage(`Could not replace the image: ${(e as Error).message}. The document is unchanged.`);
    }
    setStatus({ kind: 'ready' });
  };

  /** Turn the selection through T (a rotation around its centre, AC-ROT-1/2); checked with pdf.js like a move. */
  /** resize: set for a resize (AC-IMG-1), the new size in % of the old */
  const applyTransform = async (sel: Selection, T: number[], degrees: number, force = false, resize?: number) => {
    const s = session!;
    const targets: ObjectRef[] = sel.items.map((it) => (it.kind === 'span' ? { kind: 'span', spanIndex: it.pieces[0].index } : { kind: 'image', imageIndex: it.image.index }));
    const what = describeItems(sel.items);
    setStatus({ kind: 'busy', what: 'Turning' });
    setSelection(undefined);
    let engaged = false; // the engine changed the document: if the UI then fails, it must be taken back
    try {
      const res = await engine.current!.call({ type: 'transformObjects', pageIndex: sel.page, targets, transform: T, force });
      engaged = true;
      const next = new Uint8Array(res.bytes);
      const problems = await independentEditCheck(s.bytes, next, res.pageIndex, res.regions, s.password);
      if (problems.length && !force) {
        setHistory((await engine.current!.call({ type: 'revert' })).history);
        checkFailed('turn', problems, sel.page, targets);
        setMessage(`The turn was undone: an independent check found that something else changed (${problems.slice(0, 2).join('; ')}). The document is unchanged.`);
      } else {
        const viewer = await openViewer(next, s.password);
        if ('error' in viewer) throw viewer.error;
        showVersion({ ...s, bytes: next, viewer });
        setHistory(problems.length ? await keepForced('turn', problems, sel.page, targets) : res.history);
        changed();
        if (res.moved.length) setReselect({ page: res.pageIndex, refs: res.moved });
        const d = Math.round(Math.abs(degrees) * 10) / 10;
        const note = forcedNote(res.forced, problems);
        setMessage(resize !== undefined ? `Resized ${what} to ${resize} %.${note}` : `Turned ${what} ${d}° ${degrees < 0 ? 'left' : 'right'}.${note}`, note ? 'warn' : 'done');
      }
    } catch (e) {
      logError(e);
      if (engaged) await rollback();
      setMessage(`Could not ${resize !== undefined ? 'resize' : 'turn'} ${what}: ${(e as Error).message}. The document is unchanged.`, 'warn', forceAction(forceableOf(e), () => void applyTransform(sel, T, degrees, true, resize)));
    }
    setStatus({ kind: 'ready' });
  };

  /** The ink of selected recognised words, cut out of the page as drawn (M6 step 6), to move or copy them; undefined for other objects. */
  const inkOf = async (sel: Selection): Promise<OcrCutout[] | undefined> => {
    if (!sel.items.every((it) => it.kind === 'span' && it.pieces.every((x) => x.ocr))) return undefined;
    const s = session!;
    const info = await engine.current!.call({ type: 'spans', pageIndex: sel.page });
    const words = sel.items.flatMap((it) => (it.kind === 'span' ? it.pieces : [])).sort((p, q) => p.index - q.index);
    const size = s.sizes[sel.page];
    return cutOut(s.viewer.doc, sel.page, info.images, size.width * size.height, words.map((w) => ({ quad: [w.quads[0][0], w.quads[w.quads.length - 1][1], w.quads[w.quads.length - 1][2], w.quads[0][3]], color: w.ocr!.color })));
  };

  /**
   * Copy the selection out of the PDF to the system clipboard (AC-CLIP-1), and remember it in FlexPDF for pasting
   * (AC-PASTE-1); the document does not change.
   */
  const copy = async (sel: Selection) => {
    const targets: ObjectRef[] = sel.items.map((it) => (it.kind === 'span' ? { kind: 'span', spanIndex: it.pieces[0].index } : { kind: 'image', imageIndex: it.image.index }));
    // Recognised words are pixels of the scan: the copy takes their ink along, as a move does.
    let kept: string | undefined;
    try {
      const cutouts = await inkOf(sel);
      await engine.current!.call({ type: 'copyObjects', pageIndex: sel.page, targets, cutouts });
      // The copy's top-left as seen, in user space: a paste puts it under the mouse.
      const points = sel.items.flatMap((it) => (it.kind === 'span' ? it.pieces.flatMap((x) => x.quads.flat()) : it.image.quad));
      const images = sel.items.filter((it) => it.kind === 'image').length;
      setCopied({ kind: 'objects', page: sel.page, pastes: 0, anchor: topLeftUser(sel.page, points), lines: spansInReadingOrder(sel.items).map((it) => it.line.text), images, pic: images || cutouts ? snapshot(sel.page) : undefined, ocr: !!cutouts });
    } catch (e) {
      // Not kept for pasting: said after the clipboard copy (which, when copying is forbidden, says why itself).
      kept = (e as Error).message;
    }
    try {
      const done = await copySelection(engine.current!, sel);
      setMessage(kept ? `${done} It cannot be pasted in FlexPDF: ${kept}.` : done, kept ? 'warn' : 'done');
    } catch (e) {
      logError(e);
      setMessage(`Could not copy: ${(e as Error).message}.`);
    }
  };

  /**
   * Paste what was copied in FlexPDF onto the page in view (AC-PASTE-1): on its own page offset down and to the right
   * (further with each paste), on another page at the same place on screen. One change; the copies are selected.
   */
  // The search (AC-FIND-1): page by page, a short pause after typing; started again when the document changes. A newer
  // search (or Stop) makes an older one give up between pages.
  useEffect(() => {
    const id = ++searchId.current;
    setFound([]);
    setCurrent(0);
    setStoppedAt(undefined);
    setSearching(undefined);
    if (!findOpen || !session || !query.trim()) return;
    const n = session.sizes.length;
    const timer = setTimeout(async () => {
      const all: FoundMatch[] = [];
      try {
        for (let p = 0; p < n; p++) {
          if (searchId.current !== id) return;
          setSearching({ page: p + 1, of: n });
          const r = await engine.current!.call({ type: 'find', pageIndex: p, query, matchCase });
          if (searchId.current !== id) return;
          if (r.matches.length) {
            all.push(...r.matches.map((m) => ({ ...m, page: p, ticked: !m.locked })));
            setFound([...all]);
          }
          const at = resume.current;
          const k = at ? all.findIndex((m) => m.page > at.page || (m.page === at.page && (m.line > at.line || (m.line === at.line && m.start >= at.start)))) : -1;
          if (k >= 0) {
            setCurrent(k);
            resume.current = undefined;
          }
        }
        resume.current = undefined;
      } catch (e) {
        logError(e);
        if (searchId.current === id) setMessage(`The search stopped: ${(e as Error).message}.`);
      }
      if (searchId.current === id) setSearching(undefined);
    }, 250);
    return () => clearTimeout(timer);
  }, [findOpen, query, matchCase, session?.bytes]);
  useEffect(() => setFindFailed(new Map()), [query, matchCase, findOpen]);
  const matchKey = (m: { page: number; start: number; text: string }) => `${m.page}:${m.start}:${m.text}`;

  /**
   * Replace (M12 step 3, AC-FIND-4/5): the matches' lines edited, one edit per line (each match in it replaced), page
   * by page and in a page from its last line in the content to its first. A font question once per font, the room
   * question for the rest if asked. A line that is refused or fails its check is taken back on its own and reported;
   * the others stay, together one undo step.
   */
  const replaceMatches = async (list: FoundMatch[], force = false) => {
    if (!list.length || !session) return;
    const by = replacement;
    const groups = new Map<string, { page: number; first: number; was: string; ranges: [number, number][]; matches: FoundMatch[] }>();
    for (const m of list) {
      const key = `${m.page}:${m.spanIndex}`;
      const g = groups.get(key) ?? groups.set(key, { page: m.page, first: m.spanIndex, was: m.text, ranges: [], matches: [] }).get(key)!;
      g.ranges.push([m.start, m.end]);
      g.matches.push(m);
    }
    setStatus({ kind: 'busy', what: list.length === 1 ? 'Replacing' : `Replacing ${list.length} matches` });
    const p: Progress = { done: 0, bytes: session.bytes };
    const answers: RunAnswers = { fonts: new Map() };
    const failed = new Map(findFailed);
    let replaced = 0;
    for (const g of [...groups.values()].sort((a, b) => a.page - b.page || b.first - a.first)) {
      const one: Progress = { done: 0, bytes: p.bytes };
      try {
        await editLines(g.page, [{ first: g.first, was: g.was, text: replaceRanges(g.was, g.ranges, by) }], one, { answers, force });
        Object.assign(p, { done: p.done + one.done, bytes: one.bytes, history: one.history });
        replaced += g.matches.length;
        g.matches.forEach((m) => failed.delete(matchKey(m)));
      } catch (e) {
        await takeBack(one);
        if (!/^left out/.test((e as Error).message)) logError(e);
        g.matches.forEach((m) => failed.set(matchKey(m), { why: (e as Error).message, forceable: forceableOf(e) }));
      }
    }
    const fails = list.length - replaced;
    const plural = (n: number) => `${n} match${n === 1 ? '' : 'es'}`;
    try {
      if (p.done) await finishChanges(p);
      setMessage(replaced ? `Replaced ${plural(replaced)}${fails ? `; ${fails} could not be (see the list)` : ''}.` : `Nothing was replaced: ${[...failed.values()][0]?.why ?? 'see the list'}.`, fails ? 'warn' : 'done');
    } catch (e) {
      logError(e);
      await takeBack(p);
      setMessage(`Nothing was replaced: ${(e as Error).message}. The document is unchanged.`);
    }
    setFindFailed(failed);
    setStatus({ kind: 'ready' });
  };
  /** Replace the current match, then go on to the next one. */
  const replaceCurrent = () => {
    const m = found[current];
    if (!m) return;
    resume.current = { page: m.page, line: m.line, start: m.start + replacement.length };
    void replaceMatches([m]);
  };

  const stopSearch = () => {
    searchId.current++;
    setStoppedAt(searching);
    setSearching(undefined);
  };
  // The current match's page is brought near first (a page far away is not drawn, so its mark cannot scroll itself).
  useEffect(() => {
    const m = found[current];
    if (!findOpen || !m) return;
    document.querySelector(`section[data-testid="page-${m.page}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [current, found[current]?.page, findOpen]);
  /** Find's marks per page (only while Find is open). */
  const marks = useMemo(() => {
    const byPage = new Map<number, { quads: FoundMatch['quads']; current: boolean }[]>();
    if (findOpen) found.forEach((m, k) => (byPage.get(m.page) ?? byPage.set(m.page, []).get(m.page)!).push({ quads: m.quads, current: k === current }));
    return byPage;
  }, [findOpen, found, current]);

  /** Where the mouse last was over a page, in that page's user space (pastes land there, AC-PASTE-1). */
  const mousePlace = () => {
    const under = pointer.current && pageAt(pointer.current.x, pointer.current.y);
    const at = under && userAt(under.page, under.x, under.y);
    return under && at ? { page: under.page, at } : undefined;
  };

  /**
   * A picture from outside (D-70) or the dock becomes the copy (shown in the Context Panel); with `place` it is put on
   * the page at once (a paste, or a drop on a page).
   */
  const takePicture = async (blob: File, place?: { page: number; at?: [number, number] }) => {
    let file: File;
    try {
      file = await pictureFile(blob);
    } catch (e) {
      setMessage(`Could not use the picture: ${(e as Error).message}. Nothing was changed.`);
      return;
    }
    setCopied({ kind: 'file', file, lines: [], images: 1, pic: URL.createObjectURL(file) });
    if (place) return insertImage(file, place);
    setShowCopy(true);
    setMessage(`${file.name} is ready to paste: Paste (Ctrl+V) puts it on the page.`, 'done');
  };

  /** Ctrl+V: a picture from outside is put under the mouse and kept as the copy (D-70); otherwise FlexPDF's own copy is pasted. */
  const onPaste = (e: ClipboardEvent) => {
    if (!session || editing || status.kind !== 'ready' || confirm || overflow || residuals || !e.clipboardData) return;
    // Read now: the clipboard's data is gone once the event is over.
    const text = e.clipboardData.getData('text/plain');
    const picture = [...e.clipboardData.files].find((f) => f.type.startsWith('image/'));
    if (!picture && !copied) return;
    e.preventDefault();
    if (!canEdit) return setMessage('This document can only be viewed, so nothing can be pasted.');
    if (mode !== 'edit') return setMessage('To paste onto the page, press Edit PDF first.');
    void (async () => {
      if (picture && !(await isOwnPaste(text, picture))) await takePicture(picture, mousePlace() ?? { page: pageInView });
      else await paste();
    })();
  };
  const pasteRef = useRef(onPaste);
  pasteRef.current = onPaste;
  useEffect(() => {
    const listener = (e: ClipboardEvent) => {
      // Pasting into a text field (the line editor, the card) is the field's own.
      if (e.target instanceof HTMLElement && e.target.closest('input, textarea, select')) return;
      pasteRef.current(e);
    };
    window.addEventListener('paste', listener);
    return () => window.removeEventListener('paste', listener);
  }, []);

  const paste = async () => {
    if (!copied || !session) return;
    // A picture (from the dock or from outside): drawn under the mouse, else in the middle of the page in view.
    if (copied.kind === 'file') return insertImage(copied.file, mousePlace());
    // At the mouse (its last place over a page); else, the mouse never over a page, offset from the original on its
    // page or at the same place on the page in view.
    const under = pointer.current && pageAt(pointer.current.x, pointer.current.y);
    const at = under && placeAt(copied.page, copied.anchor, under.page, [under.x, under.y]);
    const to = at ? under!.page : pageInView;
    const step = !at && copied.page === to ? copied.pastes + 1 : 0;
    const T = at ?? crossPageTransform(copied.page, to, [PASTE_OFFSET * step, PASTE_OFFSET * step]) ?? (step ? [1, 0, 0, 1, (PASTE_OFFSET * step) / scale, (-PASTE_OFFSET * step) / scale] : [1, 0, 0, 1, 0, 0]);
    await pasteAt(to, T, step);
  };

  /** The paste itself, at the place worked out (again with force for "Do it anyway", D-45). */
  const pasteAt = async (to: number, T: number[], step: number, force = false) => {
    if (copied?.kind !== 'objects' || !session) return;
    const s = session;
    setStatus({ kind: 'busy', what: 'Pasting' });
    setSelection(undefined);
    let engaged = false; // the engine changed the document: if the UI then fails, it must be taken back
    try {
      const res = await engine.current!.call({ type: 'paste', toPage: to, transform: T, force });
      engaged = true;
      const next = new Uint8Array(res.bytes);
      const problems = await independentEditCheck(s.bytes, next, res.pageIndex, res.regions, s.password);
      if (problems.length && !force) {
        setHistory((await engine.current!.call({ type: 'revert' })).history);
        checkFailed('paste', problems);
        setMessage(`The paste was undone: an independent check found that something else changed (${problems.slice(0, 2).join('; ')}). The document is unchanged.`);
      } else {
        const p: Progress = { done: 1, bytes: next, history: problems.length ? await keepForced('paste', problems) : res.history };
        // The copy's text was changed in the Context Panel (D-69 step 3): the pasted lines are edited to it (one undo
        // step with the paste; all or nothing).
        const from = copied.from ?? copied.lines;
        const wanted = from.map((was, k) => ({ was, text: copied.lines[k] })).filter((c) => c.text !== c.was);
        let refs = res.pasted;
        if (wanted.length) {
          try {
            // The pasted lines are the last on the page (pasted content is drawn last); edits shift only what follows them.
            const first = Math.min(...res.pasted.flatMap((r) => (r.kind === 'span' ? [r.spanIndex] : [])));
            const pastedLines = (await engine.current!.call({ type: 'spans', pageIndex: to })).lines.filter((l) => l.spans[0] >= first);
            const changes = wanted.map((c) => {
              const k = pastedLines.findIndex((l) => l.text === c.was);
              if (k < 0) throw new Error(`the pasted line "${c.was}" could not be found`);
              return { first: pastedLines.splice(k, 1)[0].spans[0], was: c.was, text: c.text };
            });
            await editLines(to, changes, p);
            const now = await engine.current!.call({ type: 'spans', pageIndex: to });
            refs = [...now.lines.filter((l) => l.spans[0] >= first).map((l): ObjectRef => ({ kind: 'span', spanIndex: l.spans[0] })), ...res.pasted.filter((r) => r.kind === 'image')];
          } catch (err) {
            await takeBack(p);
            if (err !== CANCELLED) logError(err);
            setMessage(err === CANCELLED ? 'Nothing was pasted.' : `Nothing was pasted: ${(err as Error).message}. The document is unchanged.`, err === CANCELLED ? 'done' : 'warn');
            setStatus({ kind: 'ready' });
            return;
          }
        }
        await finishChanges(p);
        if (step) setCopied({ ...copied, pastes: step });
        if (refs.length) setReselect({ page: to, refs });
        // Recognised words are pasted as pictures of their ink, one per word: counted by line.
        const n = copied.ocr ? copied.lines.length : res.pasted.length;
        const note = forcedNote(res.forced, problems);
        setMessage(`Pasted ${n === 1 ? 'a copy' : `${n} copies`} on page ${to + 1}${wanted.length ? ', with the changed text' : ''}.${note}`, note ? 'warn' : 'done');
      }
    } catch (e) {
      logError(e);
      if (engaged) await rollback();
      setMessage(`Could not paste: ${(e as Error).message}. The document is unchanged.`, 'warn', forceAction(forceableOf(e), () => void pasteAt(to, T, step, true)));
    }
    setStatus({ kind: 'ready' });
  };

  /** Before export: if deleted or replaced text still occurs somewhere, list it first (AC-DEL-3). */
  const exportFile = async (forcedConfirmed = false) => {
    if (history.forced && !forcedConfirmed) {
      setSelection(undefined);
      setForcedQuestion(true);
      return;
    }
    setForcedQuestion(false);
    try {
      const r = await engine.current!.call({ type: 'residuals' });
      if (r.residuals.length) {
        setSelection(undefined);
        setResiduals(r.residuals);
        return;
      }
    } catch (e) {
      logError(e);
      setMessage(`Export failed: the document could not be searched for remaining copies of deleted text (${(e as Error).message}). Nothing was written.`);
      return;
    }
    await writeExport();
  };

  const writeExport = async () => {
    setResiduals(undefined);
    setStatus({ kind: 'busy', what: 'Exporting' });
    performance.mark('flexpdf:export-start');
    try {
      const res = await engine.current!.call({ type: 'export' });
      performance.mark('flexpdf:export-end');
      const problems = await independentExportCheck(session!.viewer.doc, new Uint8Array(res.bytes), session!.password, res.layers);
      performance.mark('flexpdf:export-checked');
      if (problems.length) throw new Error(`an independent check of the exported file failed (${problems.join('; ')})`);
      const url = URL.createObjectURL(new Blob([res.bytes], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = exportAs.current;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      setChanges((c) => ({ ...c, exported: c.made }));
      const left = res.layers?.reduce((n, l) => n + (l.left ?? 0), 0) ?? 0;
      const notes: string[] = [];
      if (left) notes.push(`${left} recognised word${left === 1 ? ' is' : 's are'} not searchable in it: the searchable text's font has no glyph for a character of ${left === 1 ? 'it' : 'them'}.`);
      // Font pruning (M7 step 2): what left the fonts, and which still hold shapes of deleted characters.
      const pruned = res.pruned;
      if (pruned?.glyphs) notes.push(`The shapes of ${pruned.glyphs} deleted character${pruned.glyphs === 1 ? ' were' : 's were'} taken out of its fonts.`);
      if (pruned?.left.length) notes.push(`Some fonts still hold the shapes of deleted characters (${pruned.left.join('; ')}).`);
      setMessage(`Exported ${a.download}. The original file is unchanged.${notes.length ? ` ${notes.join(' ')}` : ''}`, left || pruned?.left.length ? undefined : 'done');
    } catch (e) {
      logError(e);
      setMessage(`Export failed: ${(e as Error).message}. Nothing was written.`);
    }
    setStatus({ kind: 'ready' });
  };

  /** Back to the version before the last change, or forward again to the one undone (AC-UNDO-1, AC-UNDO-2). */
  const step = async (type: 'undo' | 'redo') => {
    const s = session!;
    setSelection(undefined);
    setEditing(undefined);
    setStatus({ kind: 'busy', what: type === 'undo' ? 'Undoing' : 'Redoing' });
    try {
      const res = await engine.current!.call({ type });
      const bytes = new Uint8Array(res.bytes);
      const viewer = await openViewer(bytes, s.password);
      if ('error' in viewer) throw viewer.error;
      showVersion({ ...s, bytes, viewer });
      setHistory(res.history);
      changed();
      const more = type === 'undo' ? res.history.undo : res.history.redo;
      setMessage(`${type === 'undo' ? 'Undid the last change.' : 'Redid the change.'}${more ? ` ${more} more can be ${type === 'undo' ? 'undone' : 'redone'}.` : ''}`, 'done');
    } catch (e) {
      logError(e);
      setMessage(`Could not ${type}: ${(e as Error).message}.`);
    }
    setStatus({ kind: 'ready' });
  };

  // The room beside the pages for the dock, and for the card when the widest page still fits beside it (so the pages
  // never jump when it opens). Without that room the card opens only from the dock, over the pages: by itself it
  // would cover what is being clicked.
  // The card grows with the window (D-70): 28 % of its width, from 360 to 560 px.
  const cardWidth = Math.min(560, Math.max(360, Math.round(win.w * 0.28)));
  const cardRoom = CARD_LEFT + cardWidth;
  const widest = session ? Math.max(...session.sizes.map((p) => p.width)) : 0;
  const cardFits = zoom.mode === 'fixed' ? win.w >= widest * BASE_SCALE * Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom.factor)) + 16 + cardRoom + 48 : win.w >= CARD_MIN_WINDOW;
  const sideRoom = !session ? 0 : cardFits ? cardRoom : DOCK_ROOM;
  /** the card opened or closed by the user (D-70: closed, it stays closed until opened from the dock); unset: open when it fits */
  const [cardOpen, setCardOpen] = useState<boolean>();
  /** What the card shows: the selection, else (or when asked) the copy; nothing when there is neither. */
  // The selected lines in reading order: the card's rows, and the lines its edits go to.
  const selected: CardContent | undefined = selection && { lines: spansInReadingOrder(selection.items).map((it) => it.line.text), images: selection.items.filter((it) => it.kind === 'image').length, pic: selectedPic };
  const cardContent = selected && !(showCopy && copied) ? 'selected' : copied ? 'copied' : undefined;
  // While the card asks about an edit it stays open (the selection cannot change meanwhile: the app is busy).
  // While the card asks about an edit it stays open (nothing else can change meanwhile: the app is busy).
  const cardMode = cardQuestion || (cardOpen ?? cardFits) ? cardContent : undefined;
  // The zoom in effect: a fit mode is worked out from the widest (or tallest) page and the window.
  const fitFactor = (mode: 'width' | 'page') => {
    if (!session) return 1;
    const w = Math.max(...session.sizes.map((p) => p.width));
    const h = Math.max(...session.sizes.map((p) => p.height));
    // Room around the pages: their margins and the bar at the top.
    const byWidth = (win.w - 72 - sideRoom) / (w * BASE_SCALE);
    const byHeight = (win.h - 120) / (h * BASE_SCALE);
    return mode === 'width' ? byWidth : Math.min(byWidth, byHeight);
  };
  const factor = zoom.mode === 'fixed' ? Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom.factor)) : Math.min(ZOOM_MAX, Math.max(FIT_MIN, fitFactor(zoom.mode)));
  const scale = BASE_SCALE * factor;
  /** Before the zoom changes: which page spot is in the middle of the window (beside the side panel), to keep it there. */
  const zoomAnchor = useRef<{ page: number; fx: number; fy: number; y: number }>(undefined);
  const changeZoom = (next: { mode: 'fixed' | 'width' | 'page'; factor: number }) => {
    const pages = document.querySelectorAll<HTMLElement>('[data-testid="pages"] > section');
    const el = pages[pageInView];
    if (el) {
      const r = el.getBoundingClientRect();
      const x = (window.innerWidth + sideRoom) / 2;
      const y = window.innerHeight / 2;
      zoomAnchor.current = { page: pageInView, fx: (x - r.left) / r.width, fy: (y - r.top) / r.height, y };
    }
    log({ kind: 'ui', action: 'zoom', detail: { mode: next.mode, factor: Math.round(next.factor * 100) / 100 } });
    setZoom(next);
  };
  const zoomStep = (dir: 1 | -1) => {
    const steps = dir > 0 ? ZOOM_STEPS.filter((z) => z > factor + 1e-3) : ZOOM_STEPS.filter((z) => z < factor - 1e-3).reverse();
    if (steps.length) changeZoom({ mode: 'fixed', factor: steps[0] });
  };
  // After the zoom changed: the spot that was in the middle of the window is there again.
  useLayoutEffect(() => {
    const a = zoomAnchor.current;
    zoomAnchor.current = undefined;
    const el = a && document.querySelectorAll<HTMLElement>('[data-testid="pages"] > section')[a.page];
    if (!a || !el) return;
    const r = el.getBoundingClientRect();
    // Sideways the pages area scrolls (a wide page), up and down the window.
    const pagesEl = document.querySelector<HTMLElement>('[data-testid="pages"]');
    // The middle as it is now: the zoom may have changed the room beside the pages (the card's, D-69).
    if (pagesEl) pagesEl.scrollLeft += r.left + a.fx * r.width - (window.innerWidth + sideRoom) / 2;
    window.scrollBy(0, r.top + a.fy * r.height - a.y);
  }, [scale]);
  const zoomRef = useRef(zoomStep);
  zoomRef.current = zoomStep;
  // Ctrl + mouse wheel zooms the document, not the browser (at most one step per 120 ms).
  useEffect(() => {
    let last = 0;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey || !document.querySelector('[data-testid="pages"]')) return;
      e.preventDefault();
      if (e.timeStamp - last < 120 || !e.deltaY) return;
      last = e.timeStamp;
      zoomRef.current(e.deltaY < 0 ? 1 : -1);
    };
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, []);

  const busy = status.kind !== 'ready';
  const canEdit = !!session && !session.viewOnly;
  /** the page can be changed now: the document allows it and Edit mode is on */
  const canChange = canEdit && mode === 'edit';
  const openRecent = async (r: RecentFile) => {
    const bytes = await recentBytes(r.id).catch(() => undefined);
    if (!bytes) {
      setMessage(`${r.name} is no longer in the recent files.`);
      refreshRecent();
      return;
    }
    requestOpen(new File([bytes.slice()], r.name, { type: 'application/pdf' }));
  };
  const viewClass = (v: View) => `view view-${v} ${nav.view === v ? 'is-active' : 'is-inactive'}`;
  const viewStyle = (v: View): React.CSSProperties | undefined => (nav.view === v ? undefined : { top: -nav.tops[v] });
  return (
    <div className="app">
      <div className="views">
        <div className={viewClass('home')} style={viewStyle('home')} inert={nav.view !== 'home'}>
          <HomePage
            busy={busy}
            status={status.kind === 'loading' ? 'Starting…' : status.kind === 'busy' ? `${status.what}…` : status.kind === 'error' ? status.message : ''}
            theme={theme}
            onToggleTheme={toggleTheme}
            onOpen={requestOpen}
            openName={session?.name}
            onContinue={() => goTo('editor')}
            recent={recent}
            recentOn={recentOn}
            onOpenRecent={(r) => void openRecent(r)}
            onRemoveRecent={(r) => void removeRecent(r.id).then(refreshRecent)}
            onClearRecent={() => void clearRecent().then(refreshRecent, (e: Error) => setMessage(`The recent files could not be cleared (${e.message}). They are still stored in this browser.`))}
            onLogoClick={logoClick}
            debug={debug}
            onDebug={() => setDebugPanel(true)}
            onRecentOn={(on) => {
              setRecentOn(on);
              void setRecentEnabled(on).then(refreshRecent, (e: Error) => setMessage(`The recent files could not be cleared (${e.message}). They are still stored in this browser.`));
            }}
          />
        </div>
        {/* A click beside the pages (not on one) ends the selection. */}
        <main className={viewClass('editor')} style={viewStyle('editor')} inert={nav.view !== 'editor'} onPointerDown={(e) => !busy && (e.target === e.currentTarget || (e.target as HTMLElement).dataset.testid === 'pages') && setSelection(undefined)}>
          {/* The toolbar and the questions about an edit stay at the top of the window while the pages scroll (AC-UI-1). */}
          <div data-testid="topbar" className="topbar">
            <TopBar
              status={status}
              session={session}
              page={pageInView}
              canExport={canEdit}
              fileInput={fileInput}
              onOpen={requestOpen}
              onExport={() => {
                setSelection(undefined);
                setExportName(suggestedName(session!.name, pageChanged.current));
              }}
              onHome={() => {
                setEditing(undefined);
                setSelection(undefined);
                goTo('home');
              }}
              theme={theme}
              onToggleTheme={toggleTheme}
              zoom={{ mode: zoom.mode, factor }}
              onZoomStep={zoomStep}
              debug={debug}
              onDebug={() => setDebugPanel(true)}
              onZoomSet={changeZoom}
              canEnhance={canEdit && !confirm && !overflow && !residuals}
              scanInView={pageKinds[pageInView] === 'scan' || pageKinds[pageInView] === 'layer'}
              onEnhance={() => setEnhanceMenu((m) => !m)}
              onCancelRun={ocrRun && (() => ocrRun.abort())}
              mode={canEdit ? mode : undefined}
              canMode={!confirm && !overflow && !residuals}
              onMode={() => {
                // Back to View ends an edit in progress without applying it (as Escape does).
                if (mode === 'edit') setEditing(undefined);
                setMode(mode === 'edit' ? 'view' : 'edit');
              }}
            />
            {session?.viewOnly && <ViewOnlyNotice session={session} onOwnerPassword={() => setPrompt({ name: session.name, data: session.bytes, wrong: false, owner: true })} />}
          </div>
          {session && (
            <Dock
              busy={busy}
              history={history}
              canUndoRedo={!confirm && !overflow && !residuals}
              onStep={(t) => void step(t)}
              canInsert={canChange && !confirm && !overflow && !residuals}
              onAddImage={(file) => {
                setCopied({ kind: 'file', file, lines: [], images: 1, pic: URL.createObjectURL(file) });
                setShowCopy(true);
                setMessage(`${file.name} is ready to paste: Paste (Ctrl+V) puts it on the page in view.`, 'done');
              }}
              canPaste={!!copied && canChange && !busy && !confirm && !overflow && !residuals}
              onPaste={() => void paste()}
              findOpen={findOpen}
              onFind={() => {
                setFindOpen((o) => !o);
                setFindFocus((n) => n + 1);
              }}
              card={cardContent ? { open: !!cardMode, toggle: () => setCardOpen(!cardMode) } : undefined}
            />
          )}
          {session && findOpen && (
            <FindCard
              width={cardWidth}
              query={query}
              setQuery={setQuery}
              matchCase={matchCase}
              setMatchCase={setMatchCase}
              matches={found}
              current={current}
              setCurrent={setCurrent}
              searching={searching}
              stoppedAt={stoppedAt}
              onStop={stopSearch}
              onTick={(k, on) => setFound((f) => f.map((m, j) => (j === k ? { ...m, ticked: on } : m)))}
              onClose={() => setFindOpen(false)}
              focus={findFocus}
              replacement={replacement}
              setReplacement={setReplacement}
              replace={mode === 'edit'}
              canReplace={canChange && !busy}
              onReplace={replaceCurrent}
              onReplaceAll={() => void replaceMatches(found.filter((m) => m.ticked && (!m.locked || (debug && m.lockForceable))))}
              failed={(m) => findFailed.get(matchKey(m))}
              debug={debug}
              onForce={(list) => void replaceMatches(list, true)}
              question={cardQuestion}
            />
          )}
          {session && cardMode && !findOpen && (
            <Card
              width={cardWidth}
              onClose={() => setCardOpen(false)}
              mode={cardMode}
              content={cardMode === 'selected' ? selected! : copied!}
              onApply={!canChange ? undefined : cardMode === 'selected' ? (selection ? (texts) => void applyLines(selection, texts) : undefined) : copied?.kind === 'objects' && !copied.ocr ? (texts) => void updateCopy(texts) : undefined}
              applyLabel={cardMode === 'copied' ? 'Update copy' : 'Apply'}
              busy={busy}
              question={cardQuestion}
              other={selected && copied ? (cardMode === 'selected' ? { label: `Copied: ${summary(copied)}`, onShow: () => setShowCopy(true) } : { label: `Selected: ${summary(selected)}`, onShow: () => setShowCopy(false) }) : undefined}
            />
          )}
          {session && (
            <div
              data-testid="pages"
              className="pages"
              style={{ paddingLeft: 16 + sideRoom }}
              data-page-count={session.sizes.length}
              onPointerMove={(e) => {
                if ((e.target as Element).closest('section[data-testid^="page-"]')) pointer.current = { x: e.clientX, y: e.clientY };
              }}
            >
              {session.sizes.map((size, i) => (
                <Page
                  key={`${version}-${i}`}
                  index={i}
                  size={{ width: size.width * scale, height: size.height * scale }}
                  scale={scale}
                  debug={debug}
                  doc={session.viewer.doc}
                  engine={session.engineless ? undefined : engine.current!}
                  editing={editing}
                  setEditing={startEditing}
                  onSubmit={submit}
                  selection={selection}
                  setSelection={setSelection}
                  onObject={(sel, action) => void applyObject(sel, action)}
                  onCopy={(sel) => void copy(sel)}
                  onObjectToPage={(sel, to, T) => void applyToPage(sel, to, T)}
                  onTransform={(sel, T, deg, resize) => void applyTransform(sel, T, deg, false, resize)}
                  onFill={(f, value) => void fill(f, value)}
                  onSign={(f, file) => void sign(f, file)}
                  onReplace={(sel, file) => void replaceImage(sel, file)}
                  onNote={setMessage}
                  reselect={reselect?.page === i ? reselect.refs : undefined}
                  onReselect={(sel) => {
                    setReselect(undefined);
                    setSelection(sel);
                  }}
                  disabled={busy || !!confirm || !!overflow || !!residuals || !canEdit}
                  readOnly={mode !== 'edit'}
                  textVersion={textVersion}
                  found={marks.get(i)}
                  foundCurrent={current}
                  onKind={(kind, recognised) => pageRead(i, kind, recognised)}
                  onReady={
                    i === 0
                      ? () => {
                          afterFirstPage.current?.();
                          afterFirstPage.current = undefined;
                          pendingRemember.current = undefined;
                        }
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Over both views: questions that block, the drop target, and messages. */}
      {/* The questions about an edit, next to the line being edited (AC-UX-5). */}
      {confirm && (
        <Popover
          anchor={editorAnchor}
          onCancel={() => {
            setConfirm(undefined);
            refocusEditor();
          }}
        >
          <SubstituteChoice
            plan={confirm.plan}
            onYes={() => {
              // A substitute that also does not fit: the overflow choice comes next.
              if (confirm.fit) {
                setOverflow({ edit: confirm.edit, fit: confirm.fit });
                setConfirm(undefined);
              } else void apply(confirm.edit);
            }}
            onNo={() => {
              setConfirm(undefined);
              refocusEditor();
            }}
            onPick={(face) => void submit(!!confirm.edit.force, face)}
          />
        </Popover>
      )}
      {overflow && (
        <Popover
          anchor={editorAnchor}
          onCancel={() => {
            setOverflow(undefined);
            refocusEditor();
          }}
        >
          <OverflowChoice
            fit={overflow.fit}
            onShrink={() => void apply(overflow.edit, 'shrink')}
            onKeep={() => void apply(overflow.edit, 'overflow')}
            onCancel={() => {
              setOverflow(undefined);
              refocusEditor();
            }}
          />
        </Popover>
      )}
      {enhanceMenu && session && (
        <Popover anchor={() => document.querySelector('[data-testid="enhance"]')} onCancel={() => setEnhanceMenu(false)}>
          <EnhanceMenu
            page={pageInView}
            kind={pageKinds[pageInView]}
            onPage={(again) => void enhance('page', again)}
            onAll={() => void enhance('all')}
            onClose={() => setEnhanceMenu(false)}
          />
        </Popover>
      )}
      {/* Questions that block everything else until answered (AC-UX-6). */}
      {prompt ? (
        <Modal onCancel={() => setPrompt(undefined)}>
          <PasswordForm prompt={prompt} busy={busy} onSubmit={(pw) => void openBytes(prompt.name, prompt.data, pw, prompt.owner)} onCancel={() => setPrompt(undefined)} />
        </Modal>
      ) : pendingOpen ? (
        <Modal onCancel={() => setPendingOpen(undefined)}>
          <div data-testid="confirm-replace" className="panel modal" role="dialog" aria-modal="true" aria-label="Changes not exported">
            <p>
              {session?.name} has changes that are not exported. Open {pendingOpen.name} instead? The changes will be lost.
            </p>
            <div className="actions">
              <button data-testid="confirm-replace-no" onClick={() => setPendingOpen(undefined)}>
                Cancel
              </button>
              <button
                data-testid="confirm-replace-yes"
                className="danger"
                onClick={() => {
                  setPendingOpen(undefined);
                  void open(pendingOpen);
                }}
              >
                Open {pendingOpen.name}
              </button>
            </div>
          </div>
        </Modal>
      ) : exportName !== undefined && session ? (
        <Modal onCancel={() => setExportName(undefined)}>
          <ExportNameForm
            original={session.name}
            suggested={exportName}
            onCancel={() => setExportName(undefined)}
            onExport={(name) => {
              exportAs.current = name;
              setExportName(undefined);
              void exportFile();
            }}
          />
        </Modal>
      ) : residuals ? (
        <Modal onCancel={() => setResiduals(undefined)}>
          <ResidualsDialog residuals={residuals} onExport={() => void writeExport()} onCancel={() => setResiduals(undefined)} />
        </Modal>
      ) : null}
      {forcedQuestion && (
        <Modal onCancel={() => setForcedQuestion(false)}>
          <div data-testid="forced-question" className="panel modal" role="dialog" aria-modal="true" aria-label="Forced changes">
            <p>
              {history.forced === 1 ? 'One change' : `${history.forced} changes`} in this document {history.forced === 1 ? 'was' : 'were'} made with “Do it anyway” in debug mode. FlexPDF would
              normally refuse {history.forced === 1 ? 'it' : 'them'}, or could not check the result: the exported copy may not be faithful (for example when text is copied or
              searched, read aloud, or a layer is hidden).
            </p>
            <div className="actions">
              <button data-testid="forced-cancel" onClick={() => setForcedQuestion(false)}>
                Cancel
              </button>
              <button data-testid="forced-export" onClick={() => void exportFile(true)}>
                Export anyway
              </button>
            </div>
          </div>
        </Modal>
      )}
      {debugPanel && (
        <Modal onCancel={() => setDebugPanel(false)}>
          <DebugPanel
            onClose={() => setDebugPanel(false)}
            onDebugOff={() => {
              setDebugOn(false);
              setDebug(false);
              setDebugPanel(false);
            }}
          />
        </Modal>
      )}
      {dragging && (
        <div data-testid="drop-overlay" className="drop-overlay">
          <div>Drop a PDF to open it, or a picture to add it</div>
        </div>
      )}
      <div className="toasts">
        {message && (
          <div key={message.id} data-testid="message" data-kind={message.kind} className={`toast toast-${message.kind}`} role={message.kind === 'warn' ? 'alert' : 'status'}>
            <span>{message.text}</span>
            {message.action && (
              <button
                data-testid="message-action"
                className="toast-action"
                onClick={() => {
                  const run = message.action!.run;
                  setMessage(undefined);
                  run();
                }}
              >
                {message.action.label}
              </button>
            )}
            <button className="toast-close" aria-label="Dismiss" onClick={() => setMessage(undefined)}>
              <CloseIcon size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
